// client/src/utils/lineTax.ts
//
// Filling in a per-line tax amount the invoice never printed.
//
// MIRRORED LOGIC: src/utils/invoiceVat.js `repairZeroLineTax` — read the header
// comment there for the full story (invoice 076580). The short version: an
// invoice that flags its lines "V" and prints VAT only once at the foot leaves
// the extractor nothing to put in `taxAmount`, and it returns 0 rather than
// null. Every derivation in the pipeline was guarded on `null`, so a 0 was
// treated as a figure the supplier had stated and the line kept "12.5%" and
// "0.00" side by side for ever.
//
// The server repairs this the moment an invoice is processed. This copy repairs
// the ones already in the database, when someone opens them — same rule, same
// proof, so the two cannot disagree about what a line's tax should be.
//
// The proof matters: rather than assume a 0 means "missing", the amounts are
// filled in and kept ONLY if the lines then reproduce the tax total the
// document itself prints. A genuinely zero-rated line on a mixed invoice makes
// that sum overshoot, so nothing is written and the discrepancy stays visible.

import { num, taxAmountForRate, votedConvention, discountReadings, lineQuantity } from "./invoiceLineMath";
import type { Convention } from "./invoiceLineMath";

export type TaxFill = {
  index: number;
  taxAmount: number;
  /** 'reconciled' — proven against the invoice's own tax total. 'derived' — no total to check. */
  source: "reconciled" | "derived";
};

/**
 * The amount a line's tax is charged on: qty x price, or the printed extension.
 * A price worked out from the printed total: the base it was divided from,
 * as the server's `invoiceVat.derivedPriceBase` (qty x a 4dp price can land a
 * cent off it).
 *
 * A discounted line is taxed on what is paid, as the server's
 * `taxableBaseReadings` first reading: the printed total where it agrees with
 * the gross less the discount (as an amount or a percent), else the gross less
 * the amount. 12 x 25.00 less 50.00 at 12.5% owes 31.25 on 250.00, not 37.50
 * on 300.00 — this copy used to ignore the discount and fill the 37.50.
 */
export function taxableBaseOf(line: any): number | null {
  const readings = taxableBaseReadings(line);
  return readings.length ? readings[0] : null;
}

/**
 * The server's `invoiceVat.taxableBaseReadings`: every reading of a unitless
 * discount — off the same shared `discountReadings` (invoiceLineMath.ts) that
 * builds `computeLineMath`'s `bases` — most likely first: the printed total
 * where it corroborates one, the gross less the amount, less the percent, less
 * the gross, THEN less it per unit (see the server's own comment on this order
 * — `repairZeroLineTax` / `planZeroTaxRepairs` pick the SAME index on every
 * gap line in one pass, so per-unit has to sit last or it shifts every index
 * after it).
 */
function taxableBaseReadings(line: any): number[] {
  const gross = grossBaseOf(line);
  if (gross === null) return [];
  const discount = num(line?.discount);
  if (discount === null || discount === 0) return [gross];
  const { wholeLine: lessAmount, perUnit: lessPerUnit, percent: lessPercent } = discountReadings(
    gross,
    lineQuantity(line),
    discount
  );
  const total = num(line?.lineTotal ?? line?.total ?? line?.amount);
  const printed = total === null ? null : Number(total.toFixed(2));
  const corroborated =
    printed !== null &&
    [lessAmount, lessPerUnit, lessPercent].some((v) => v !== null && Math.abs(v - printed) <= 0.02);
  const readings: number[] = corroborated ? [printed as number] : [];
  readings.push(lessAmount as number);
  if (lessPercent !== null) readings.push(lessPercent);
  readings.push(gross);
  if (lessPerUnit !== null) readings.push(lessPerUnit);
  return readings.filter((v, i, all) => all.indexOf(v) === i);
}

function grossBaseOf(line: any): number | null {
  const derived = line?.unitPriceFromLineTotalBase;
  if (line?.unitPriceFromLineTotal === true && typeof derived === "number" && Number.isFinite(derived)) return derived;
  const qty = lineQuantity(line);
  const unitPrice = num(line?.unitPrice);
  if (qty !== null && unitPrice !== null) return Number((qty * unitPrice).toFixed(2));
  const total = num(line?.lineTotal ?? line?.total ?? line?.amount);
  return total === null ? null : Number(total.toFixed(2));
}

/**
 * The VAT a line owes at `rate` on `base` — the server's `invoiceVat.lineTaxForRate`.
 * A line with no qty x price and no derived price is based on its printed
 * total; on an invoice whose priced lines vote VAT-inclusive (`votedConvention`)
 * that total already holds the VAT, so 112.50 at 12.5% owes 12.50, not 14.06.
 * Exclusive or unknown: the total is the net, as before.
 */
function lineTaxForRate(line: any, rate: number, convention: Convention | null, base: number): number | null {
  const priced = lineQuantity(line) !== null && num(line?.unitPrice) !== null;
  const derived = line?.unitPriceFromLineTotal === true && Number.isFinite(line?.unitPriceFromLineTotalBase);
  return taxAmountForRate(base, rate, { inclusive: convention === "inclusive" && !priced && !derived });
}

/**
 * A line whose tax amount is the 0 an extractor puts in for "not printed",
 * beside a positive rate the document stated — the server's
 * `repairZeroLineTax` gap. An EMPTY amount is not one: missing is not zero,
 * and the server leaves it alone (extraction derives it; an edit that clears
 * it is a person's), so filling it here wrote what the server never would.
 */
function isGap(line: any): boolean {
  if (num(line?.taxAmount) !== 0) return false;
  if (line?.taxRateDefaulted === true || line?.taxRateUnresolved === true) return false;
  const rate = num(line?.taxRate);
  if (rate === null || rate <= 0) return false;
  const base = taxableBaseOf(line);
  return base !== null && base > 0;
}

/**
 * What to write, if anything. An empty `fills` means either nothing was missing
 * or filling it would contradict the invoice's own total — in both cases the
 * right action is to write nothing.
 */
export function planZeroTaxRepairs(
  lines: any[],
  taxTotal: any
): { fills: TaxFill[]; note: string | null } {
  const items = Array.isArray(lines) ? lines : [];
  const convention = votedConvention(items);
  const gapIndexes = items.map((li, index) => (isGap(li) ? index : -1)).filter((i) => i >= 0);

  /** Every gap filled from the nth reading of its discount (the server's `fillWith`). */
  const fillWith = (reading: number): TaxFill[] =>
    gapIndexes.map((index) => {
      const li = items[index];
      const bases = taxableBaseReadings(li);
      const base = bases[Math.min(reading, bases.length - 1)];
      const rate = num(li?.taxRate) as number;
      return { index, taxAmount: lineTaxForRate(li, rate, convention, base) as number, source: "derived" };
    });

  if (!gapIndexes.length) return { fills: [], note: null };
  const gaps = fillWith(0);

  const stated = num(taxTotal);
  if (stated === null || stated <= 0) {
    return {
      fills: gaps,
      note: `${gaps.length} line(s) had a rate but no tax amount; calculated from each line's own rate.`,
    };
  }

  const sumWith = (fills: TaxFill[]) => {
    const byIndex = new Map(fills.map((g) => [g.index, g.taxAmount]));
    return Number(
      items
        .reduce((acc, li, i) => acc + (byIndex.has(i) ? (byIndex.get(i) as number) : num(li?.taxAmount) || 0), 0)
        .toFixed(2)
    );
  };
  const tolerance = Math.max(0.5, Math.abs(stated) * 0.01);

  // Each reading of the discount column in turn, keeping the first that
  // reproduces the printed total — as the server's repairZeroLineTax. Only
  // the invoice can say which reading the column meant.
  const readings = Math.max(...gapIndexes.map((i) => taxableBaseReadings(items[i]).length), 1);
  let filled = gaps;
  let summed = sumWith(gaps);
  for (let reading = 1; reading < readings && Math.abs(summed - stated) > tolerance; reading += 1) {
    const attempt = fillWith(reading);
    const total = sumWith(attempt);
    if (Math.abs(total - stated) <= tolerance) {
      filled = attempt;
      summed = total;
    }
  }

  if (Math.abs(summed - stated) > tolerance) {
    return {
      fills: [],
      note:
        `${gaps.length} line(s) have a rate but no tax amount. Filling them would make the ` +
        `lines sum to ${summed} against a printed tax total of ${stated}, so they were left alone.`,
    };
  }

  return {
    fills: filled.map((g) => ({ ...g, source: "reconciled" as const })),
    note: `${gaps.length} line(s) had a rate but no tax amount; the calculated figures reproduce the invoice's tax total of ${stated}.`,
  };
}

/**
 * A CLIENT-invoice line's VAT amount (BUSINESS_RULES#client-invoice-line-vat).
 *
 * Decided 2026-09-22 ("2026-09-22 one-owner audit — vessels are never charged
 * VAT"): USC never adds output VAT to what a vessel is billed or quoted. This
 * is the one owner of that answer for every call site in ClientInvoicePage.tsx
 * (t11's four inline copies, since collapsed to calls here) — a line's, or an
 * imported vendor-bill line's, `vatApplied`/`vatPercent`/`taxAmount` no longer
 * change the answer: it is always zero.
 *
 * Do not confuse this with INPUT VAT: the 12.5% USC pays ITS suppliers is a
 * cost, and is untouched — it is read on the vendor-invoice page through
 * `planZeroTaxRepairs`/`taxableBaseOf` above, a different function entirely.
 *
 * The parameters are kept on the signature (schema field names stay, per
 * D45.12) even though none of them can produce a nonzero result any more —
 * every call site keeps passing its already-decided applied/percent/base and
 * a vendor-bill's stated amount unchanged, so no caller has to be rewritten
 * around a boundary that moved.
 */
export function resolveVatAmount(_opts: {
  vatApplied: boolean;
  vatPercent: number;
  taxableBase: number | null;
  statedAmount?: number | string | null;
}): number {
  return 0;
}
